/**
 * Everything the Product analytics page keeps in its address: the tab, the
 * Customers view and the filters, so a link opens the same view. Values are
 * non-numeric words where they can be, because the router quotes strings that
 * look like numbers; the customer id is kept as a number for the same reason.
 */

import {
  choiceList,
  choiceListParam,
  DEVICE_CHOICES,
  VISIT_REASONS,
  type DeviceChoice,
  type VisitReason,
} from "./searchChoiceLists";
import { customerParam, pickedIds, typeIds } from "./searchIdLists";

export type ProductAnalyticsTab = "funnel" | "ratings" | "customers" | "customer-view";
export type CustomersView = "compare" | "growth" | "product";
export type AnalyticsPeriod = "7d" | "30d" | "90d" | "180d" | "365d" | "custom";
export type Granularity = "day" | "week" | "month";
/** Thumbs are stored as 1 and 5, so they fall into the low and high groups. */
export type RatingGroup = "unrated" | "low" | "mid" | "high";
export type CompareWith = "previous" | "yearEarlier";
/** The Customers tab's rows: one per customer, or one per main customer type. */
export type CustomerRows = "customers" | "types";
/** What started the conversation, as the server groups `conversation_source`. */
export type EntryPoint = "button" | "search" | "embedded" | "link" | "shop";

export const RATING_GROUPS: readonly RatingGroup[] = ["unrated", "low", "mid", "high"];
export const ENTRY_POINT_IDS: readonly EntryPoint[] = [
  "button",
  "search",
  "embedded",
  "link",
  "shop",
];

export interface ProductAnalyticsSearch {
  tab: ProductAnalyticsTab;
  customersView: CustomersView;
  period: AnalyticsPeriod;
  from: string | null;
  to: string | null;
  /** The group of customers above the message threshold, or one organization id. */
  customer: "group" | number;
  /** The Customers tab's pick from the group: null keeps all of it, [] none. */
  picked: number[] | null;
  /**
   * Customer type ids the group is narrowed to, main or extra type (`untyped`
   * for customers without one): null keeps every customer, [] none.
   */
  types: string[] | null;
  rows: CustomerRows;
  by: Granularity;
  /** The rating groups kept: all of them unless narrowed, none shows nothing. */
  ratings: RatingGroup[];
  /** The optional "Clicked a product" funnel step. */
  clickStep: boolean;
  /** The optional "Paid" funnel step. */
  paidStep: boolean;
  /** The entry points kept, like `ratings`. */
  entries: EntryPoint[];
  /** Why they came, kept like `ratings`; Funnel and Customer view share it. */
  reasons: VisitReason[];
  /** The devices kept, like `reasons`. */
  devices: DeviceChoice[];
  compare: CompareWith;
}

export const DEFAULT_SEARCH: ProductAnalyticsSearch = {
  tab: "funnel",
  customersView: "compare",
  period: "30d",
  from: null,
  to: null,
  customer: "group",
  picked: null,
  types: null,
  rows: "customers",
  by: "week",
  ratings: [...RATING_GROUPS],
  clickStep: false,
  paidStep: false,
  entries: [...ENTRY_POINT_IDS],
  reasons: [...VISIT_REASONS],
  devices: [...DEVICE_CHOICES],
  compare: "previous",
};

/** Ratings and Customers count by week or month; a day chosen on the Funnel tab reads as a week. */
export function weekOrMonth(by: Granularity): "week" | "month" {
  return by === "month" ? "month" : "week";
}

export const PERIOD_DAYS: Record<Exclude<AnalyticsPeriod, "custom">, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  "180d": 180,
  "365d": 365,
};

function oneOf<T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T {
  return typeof raw === "string" && (allowed as readonly string[]).includes(raw)
    ? (raw as T)
    : fallback;
}

function isoDate(raw: unknown): string | null {
  return typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
}

export function parseProductAnalyticsSearch(
  search: Record<string, unknown>,
): ProductAnalyticsSearch {
  const period = oneOf(search.period, ["7d", "30d", "90d", "180d", "365d", "custom"], "30d");
  const from = isoDate(search.from);
  const to = isoDate(search.to);
  const customRange = period === "custom" && from !== null && to !== null && from <= to;
  return {
    tab: oneOf(search.tab, ["funnel", "ratings", "customers", "customer-view"], DEFAULT_SEARCH.tab),
    customersView: oneOf(
      search.customers,
      ["compare", "growth", "product"],
      DEFAULT_SEARCH.customersView,
    ),
    // "Custom" without a finished range keeps the picker open on the default days.
    period,
    from: customRange ? from : null,
    to: customRange ? to : null,
    customer: customerParam(search.customer),
    picked: pickedIds(search.picked),
    types: typeIds(search.types),
    rows: oneOf(search.rows, ["customers", "types"], DEFAULT_SEARCH.rows),
    by: oneOf(search.by, ["day", "week", "month"], DEFAULT_SEARCH.by),
    ratings: choiceList(search.rating, RATING_GROUPS),
    clickStep: search.click === true || search.click === "true",
    paidStep: search.paid === true || search.paid === "true",
    entries: choiceList(search.entry, ENTRY_POINT_IDS),
    reasons: choiceList(search.reason, VISIT_REASONS),
    devices: choiceList(search.device, DEVICE_CHOICES),
    compare: oneOf(search.compare, ["previous", "yearEarlier"], DEFAULT_SEARCH.compare),
  };
}

/** The address form. Every field is optional, so a plain `/product-analytics` link is valid. */
export interface ProductAnalyticsSearchParams {
  tab?: ProductAnalyticsTab;
  customers?: CustomersView;
  period?: AnalyticsPeriod;
  from?: string;
  to?: string;
  customer?: number;
  /** The Customers tab's pick, comma-separated ids or `none`. */
  picked?: string;
  /** Customer type ids, comma-separated, or `none`. */
  types?: string;
  rows?: CustomerRows;
  by?: Granularity;
  /** Rating groups, comma-separated, or `none`. */
  rating?: string;
  click?: boolean;
  paid?: boolean;
  /** Entry points, comma-separated, or `none`. */
  entry?: string;
  /** Why they came, comma-separated, or `none`. */
  reason?: string;
  /** Devices, comma-separated, or `none`. */
  device?: string;
  compare?: CompareWith;
}

/** What the route validates: the parsed state written back without its defaults. */
export function validateProductAnalyticsSearch(
  search: Record<string, unknown>,
): ProductAnalyticsSearchParams {
  return serializeProductAnalyticsSearch(parseProductAnalyticsSearch(search));
}

/** The address form: defaults are left out so a plain link stays plain. */
export function serializeProductAnalyticsSearch(
  state: ProductAnalyticsSearch,
): ProductAnalyticsSearchParams {
  const out: ProductAnalyticsSearchParams = {};
  if (state.tab !== DEFAULT_SEARCH.tab) out.tab = state.tab;
  if (state.customersView !== DEFAULT_SEARCH.customersView) out.customers = state.customersView;
  if (state.period !== DEFAULT_SEARCH.period) out.period = state.period;
  if (state.period === "custom" && state.from && state.to) {
    out.from = state.from;
    out.to = state.to;
  }
  if (state.customer !== "group") out.customer = state.customer;
  if (state.picked !== null) out.picked = state.picked.length ? state.picked.join(",") : "none";
  if (state.types !== null) out.types = state.types.length ? state.types.join(",") : "none";
  if (state.rows !== DEFAULT_SEARCH.rows) out.rows = state.rows;
  if (state.by !== DEFAULT_SEARCH.by) out.by = state.by;
  const rating = choiceListParam(state.ratings, RATING_GROUPS);
  if (rating) out.rating = rating;
  if (state.clickStep) out.click = true;
  if (state.paidStep) out.paid = true;
  const entry = choiceListParam(state.entries, ENTRY_POINT_IDS);
  if (entry) out.entry = entry;
  const reason = choiceListParam(state.reasons, VISIT_REASONS);
  if (reason) out.reason = reason;
  const device = choiceListParam(state.devices, DEVICE_CHOICES);
  if (device) out.device = device;
  if (state.compare !== DEFAULT_SEARCH.compare) out.compare = state.compare;
  return out;
}

function localDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * The `from`/`to` the API gets: a custom range as picked, otherwise the last
 * full days before today, the same default the server applies.
 */
export function periodRange(
  state: Pick<ProductAnalyticsSearch, "period" | "from" | "to">,
  today: Date = new Date(),
): { from: string; to: string } {
  if (state.period === "custom" && state.from && state.to) {
    return { from: state.from, to: state.to };
  }
  const days = PERIOD_DAYS[state.period === "custom" ? "30d" : state.period];
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - days + 1);
  return { from: localDate(start), to: localDate(end) };
}
